import React from "react";
import { Tooltip, makeStyles } from "@material-ui/core";

const channelConfig = {
  baileys: {
    label: "WhatsApp",
    bg: "#dcfce7",
    border: "#86efac",
    color: "#15803d",
    icon: "whatsapp"
  },
  waba: {
    label: "WhatsApp Business",
    bg: "#d1fae5",
    border: "#6ee7b7",
    color: "#047857",
    icon: "business"
  },
  instagram: {
    label: "Instagram",
    bg: "#fce7f3",
    border: "#f9a8d4",
    color: "#be185d",
    icon: "instagram"
  },
  messenger: {
    label: "Messenger",
    bg: "#dbeafe",
    border: "#93c5fd",
    color: "#1d4ed8",
    icon: "messenger"
  },
  webchat: {
    label: "WebChat",
    bg: "#e0f2fe",
    border: "#7dd3fc",
    color: "#0369a1",
    icon: "chat"
  }
};

const useStyles = makeStyles({
  root: {
    "--channel-bg": props => props.config.bg,
    "--channel-border": props => props.config.border,
    "--channel-color": props => props.config.color,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: props => (props.showLabel ? 0 : props.size),
    height: props => props.size,
    padding: props => (props.showLabel ? `0 ${Math.max(7, props.size / 3)}px` : 0),
    borderRadius: 999,
    backgroundColor: "transparent",
    border: 0,
    color: "var(--channel-color)",
    fontSize: props => Math.max(10, props.size * 0.42),
    fontWeight: 800,
    lineHeight: 1,
    flex: "none",
    boxShadow: "none"
  },
  icon: {
    width: props => props.size,
    height: props => props.size,
    display: "block"
  },
  text: {
    minWidth: 0,
    maxWidth: props => props.maxLabelWidth,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  }
});

const normalizeConnectionType = whatsapp => {
  const type = String(whatsapp?.connectionType || "").toLowerCase();
  const name = String(whatsapp?.name || "").toLowerCase();

  if (type === "waba") return "waba";
  if (type === "instagram") return "instagram";
  if (type === "messenger") return "messenger";
  if (type === "webchat") return "webchat";
  if (whatsapp?.metaInstagramBusinessId || name.includes("instagram")) return "instagram";
  if (whatsapp?.metaPageId || name.includes("messenger") || name.includes("facebook")) return "messenger";
  if (whatsapp?.wabaEnabled || name.includes("waba") || name.includes("business")) return "waba";
  return "baileys";
};

const ChannelIcon = ({ type, className }) => {
  if (type === "instagram") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <rect x="5" y="5" width="14" height="14" rx="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="16.6" cy="7.4" r="1.2" fill="currentColor" />
      </svg>
    );
  }

  if (type === "messenger") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path
          d="M12 4.5c-4.5 0-8 3.1-8 7.1 0 2.3 1.1 4.3 3 5.6v2.3l2.4-1.3c.8.2 1.7.4 2.6.4 4.5 0 8-3.1 8-7.1s-3.5-7-8-7Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="m8 13.2 2.6-2.8 2.4 2.1 3-3.1-2.7 4.2-2.4-2.1-2.9 1.7Z" fill="currentColor" />
      </svg>
    );
  }

  if (type === "waba") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path
          d="M12 4.5a7.5 7.5 0 0 0-6.4 11.4L4.8 19l3.2-.8A7.5 7.5 0 1 0 12 4.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M9.2 8.2h3.2c1.4 0 2.2.7 2.2 1.8 0 .7-.3 1.2-.9 1.5.8.3 1.2.9 1.2 1.8 0 1.3-1 2.1-2.5 2.1H9.2V8.2Zm2 2.9h.9c.5 0 .8-.2.8-.7s-.3-.7-.8-.7h-.9v1.4Zm0 2.8h1.1c.6 0 .9-.3.9-.8 0-.5-.3-.8-.9-.8h-1.1v1.6Z" fill="currentColor" />
      </svg>
    );
  }

  if (type === "webchat") {
    return (
      <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path
          d="M6 5.5h12c1.4 0 2.5 1.1 2.5 2.5v7c0 1.4-1.1 2.5-2.5 2.5h-4.8l-3.4 2.8a.9.9 0 0 1-1.5-.7v-2.1H6A2.5 2.5 0 0 1 3.5 15V8c0-1.4 1.1-2.5 2.5-2.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <circle cx="9" cy="11.5" r="1.1" fill="currentColor" />
        <circle cx="12" cy="11.5" r="1.1" fill="currentColor" />
        <circle cx="15" cy="11.5" r="1.1" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M12 4.5a7.5 7.5 0 0 0-6.4 11.4L4.8 19l3.2-.8A7.5 7.5 0 1 0 12 4.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M9.2 8.4c.3-.2.7-.1.9.2l.8 1.4c.2.3.1.7-.1 1l-.4.4c.5.9 1.3 1.7 2.2 2.2l.4-.4c.3-.3.7-.3 1-.1l1.4.8c.3.2.4.6.2.9-.4.7-1.2 1.1-2 1-2.7-.4-4.9-2.5-5.3-5.3-.2-.9.2-1.7.9-2.1Z"
        fill="currentColor"
      />
    </svg>
  );
};

const ConnectionChannelBadge = ({
  whatsapp,
  size = 28
}) => {
  const type = normalizeConnectionType(whatsapp);
  const config = channelConfig[type] || channelConfig.baileys;
  const classes = useStyles({ config, showLabel: false, size, maxLabelWidth: 0 });
  const connectionName = String(whatsapp?.name || "").trim();
  const tooltipTitle = connectionName
    ? `${config.label}: ${connectionName}`
    : config.label;

  return (
    <Tooltip arrow title={tooltipTitle}>
      <span className={classes.root}>
        <ChannelIcon type={type} className={classes.icon} />
      </span>
    </Tooltip>
  );
};

export default ConnectionChannelBadge;
